import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { getCurrentAuthUser } from "./authRepository";

const COLLECTION = "arenaInvitations";

function invitationRef(invitationId) {
  return doc(db, COLLECTION, invitationId);
}

function fromSnap(snap) {
  if (!snap.exists()) return null;
  return { ...snap.data(), invitationId: snap.id };
}

/**
 * @param {object} invitation
 */
export async function createArenaInvitation(invitation) {
  const id = invitation.invitationId;
  if (!id) throw new Error("invitationId required");
  await setDoc(invitationRef(id), {
    ...invitation,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return invitation;
}

/**
 * @param {string} invitationId
 */
export async function fetchArenaInvitation(invitationId) {
  if (!invitationId) return null;
  const snap = await getDoc(invitationRef(invitationId));
  return fromSnap(snap);
}

/**
 * @param {string} invitationId
 * @param {object} patch
 */
export async function updateArenaInvitation(invitationId, patch) {
  await updateDoc(invitationRef(invitationId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Organizer list — scoped to invitations this user created (rules-safe query).
 * @param {string} arenaId
 * @param {{ max?: number; invitedBy?: string | null }} [options]
 */
export async function listArenaInvitationsForArena(arenaId, options = {}) {
  const max = Math.min(options.max || 60, 100);
  if (!arenaId) return [];
  const invitedBy =
    options.invitedBy !== undefined
      ? options.invitedBy
      : getCurrentAuthUser()?.uid || null;
  if (!invitedBy) return [];
  const qref = query(
    collection(db, COLLECTION),
    where("arenaId", "==", arenaId),
    where("invitedBy", "==", invitedBy),
    orderBy("createdAtMs", "desc"),
    limit(max)
  );
  const snap = await getDocs(qref);
  return snap.docs.map((d) => ({ ...d.data(), invitationId: d.id }));
}

/**
 * @param {string} invitedUserId
 * @param {{ max?: number }} [options]
 */
export async function listPendingArenaInvitationsForUser(
  invitedUserId,
  options = {}
) {
  const max = Math.min(options.max || 40, 80);
  if (!invitedUserId) return [];
  const qref = query(
    collection(db, COLLECTION),
    where("invitedUserId", "==", invitedUserId),
    where("status", "==", "pending"),
    orderBy("createdAtMs", "desc"),
    limit(max)
  );
  const snap = await getDocs(qref);
  return snap.docs.map((d) => ({ ...d.data(), invitationId: d.id }));
}

/**
 * Live list of invitations for an arena (organizer status board).
 * @param {string} arenaId
 * @param {(list: object[]) => void} onChange
 * @returns {() => void}
 */
export function subscribeArenaInvitations(arenaId, onChange) {
  const invitedBy = getCurrentAuthUser()?.uid || null;
  if (!arenaId || !invitedBy) {
    onChange([]);
    return () => {};
  }
  const qref = query(
    collection(db, COLLECTION),
    where("arenaId", "==", arenaId),
    where("invitedBy", "==", invitedBy),
    orderBy("createdAtMs", "desc"),
    limit(60)
  );
  return onSnapshot(
    qref,
    (snap) => {
      onChange(snap.docs.map((d) => ({ ...d.data(), invitationId: d.id })));
    },
    () => onChange([])
  );
}

/**
 * Live pending invitations for the signed-in invitee.
 * @param {string} invitedUserId
 * @param {(list: object[]) => void} onChange
 * @returns {() => void}
 */
export function subscribePendingArenaInvitationsForUser(
  invitedUserId,
  onChange
) {
  if (!invitedUserId) {
    onChange([]);
    return () => {};
  }
  const qref = query(
    collection(db, COLLECTION),
    where("invitedUserId", "==", invitedUserId),
    where("status", "==", "pending"),
    orderBy("createdAtMs", "desc"),
    limit(40)
  );
  return onSnapshot(
    qref,
    (snap) => {
      onChange(snap.docs.map((d) => ({ ...d.data(), invitationId: d.id })));
    },
    () => onChange([])
  );
}
